import React from 'react';

import {ROLES} from './Menu';
import {OptionContentType} from './MenuConstants';
import OptionContainer, {OptionContainerProps} from './OptionContainer';
import OptionContent, {OptionContentProps} from './OptionContent';

export type OptionProps<T> = OptionContentProps & OptionContainerProps<T>;

/**
 * This Option component is used for Menu options and supports icons and metadata.
 *
 * Renders OptionContainer and OptionContent, which can be used instead for components like SearchDropdown
 * that want to handle interactions and presentational content separately.
 *
 * To make an Option which copies text to the clipboard on select, use the `onSelect` prop and the
 * `copyToClipboard` function from util/copyToClipboard. *Do not wrap the Option children with <Clipboard>
 * as the Clipboard will not take the entire space of the Option, so clicking on the edges of the Option will
 * not properly copy to clipboard. Example: proteins/alignments/ProteinAlignmentToolbar.js:138.
 *
 * See /_design/dropdowns to see example usages.
 */
export default function Option<T>(props: OptionProps<T>): JSX.Element {
  return (
    <OptionContainer
      className={props.className}
      linkClassName={props.linkClassName}
      dataMethod={props.dataMethod}
      download={props.download}
      href={props.href}
      openInNewTab={props.openInNewTab}
      contentType={props.contentType}
      isDisabled={props.isDisabled}
      isHovered={props.isHovered}
      isSelected={props.isSelected}
      onHover={props.onHover}
      onMouseOut={props.onMouseOut}
      onMouseUp={props.onMouseUp}
      onSelect={props.onSelect}
      shouldEllipsize={props.shouldEllipsize}
      hasBorder={props.hasBorder}
      heightOverride={props.heightOverride}
      value={props.value}
      _submenu={props._submenu}
      disableEventPropagation={props.disableEventPropagation}
      optionRef={props.optionRef}
    >
      <OptionContent
        avatar={props.avatar}
        avatarClassName={props.avatarClassName}
        icon={props.icon}
        iconClassName={props.iconClassName}
        iconWrapperClassName={props.iconWrapperClassName}
        className={props.contentClassName}
        childrenClassName={props.childrenClassName}
        metadata={props.metadata}
        optionNameClassName={props.optionNameClassName}
        optionMetadataClassName={props.optionMetadataClassName}
        optionChevronClassName={props.optionChevronClassName}
        tooltipTitle={props.tooltipTitle}
        tooltipPlacement={props.tooltipPlacement}
        hasSubmenu={Boolean(props._submenu)}
        dataTestAttrs={props.dataTestAttrs}
        titleAttr={
          // The `title` attribute is presented as a tooltip, and is only useful when the menu item may
          // be ellipsized
          props.shouldEllipsize && typeof props.children === 'string' ? props.children : undefined
        }
      >
        {props.children}
      </OptionContent>
    </OptionContainer>
  );
}
Option.defaultProps = {
  _role: ROLES.OPTION,
  contentType: OptionContentType.ANCHOR,
  shouldEllipsize: false,
  disableEventPropagation: false,
};
